import React from 'react';
import { Sliders } from 'lucide-react';
import MetricGroup, { MetricField } from '../MetricGroup';
import type { ServerStats } from '../../../types';
import { getLevel, ThresholdLevel } from '../../../utils/thresholds';

interface Props {
  serverStats: ServerStats | null;
}

const VbvRc: React.FC<Props> = ({ serverStats }) => {
  const sv = serverStats;
  const dominant = sv?.dominant_engine || 'default';
  const ed = sv?.per_engine && dominant !== 'default'
    ? (sv.per_engine as any)[dominant]
    : null;

  const vbvBuf = ed?.vbv_buf_kbits ?? sv?.vbv_buf_kbits ?? 0;
  const vbvMaxRate = ed?.vbv_max_rate_kbps ?? sv?.vbv_max_rate_kbps ?? 0;
  const vbvMult = ed?.vbv_multiplier ?? sv?.vbv_multiplier ?? -1;
  const vbvCapped = (ed?.vbv_capped ?? sv?.vbv_capped) === true;

  // libaom AV1 / OpenH264 没有 VBV
  const noVbv = /libaom|openh264/.test(dominant);

  const cappedLevel: ThresholdLevel = vbvCapped ? 'warn' : 'normal';

  // NVENC reconfig 失败次数
  const reconfigFail = ed?.nvenc_reconfig?.failure_count_total ?? 0;
  const reconfigCount = ed?.nvenc_reconfig?.count_total ?? 0;
  const reconfigLevel: ThresholdLevel = reconfigFail > 0 ? 'crit' : 'normal';

  const cardLevel: ThresholdLevel =
    reconfigLevel === 'crit' ? 'crit' : cappedLevel;

  const fields: MetricField[] = [];

  if (noVbv) {
    fields.push({ label: 'VBV 状态', value: 'N/A', tooltip: 'libaom AV1 / OpenH264 不暴露 VBV' });
  } else {
    fields.push({ label: 'VBV 缓冲', value: vbvBuf > 0 ? vbvBuf : '—', unit: vbvBuf > 0 ? 'kb' : '' });
    fields.push({ label: '上限', value: vbvMaxRate, unit: 'kbps' });
    if (vbvMult >= 0) {
      fields.push({ label: 'x264 multiplier', value: vbvMult, tooltip: '2-16 档位（log 映射）' });
    }
    fields.push({
      label: 'VBV cap',
      value: vbvCapped ? '触发' : '正常',
      level: cappedLevel,
      tooltip: vbvCapped ? '撞 1500kb 上限，突发能力被压扁' : '未触发上限',
    });
  }

  // NVENC 专属：reconfig 状态
  if (dominant.startsWith('nvenc_')) {
    fields.push({
      label: 'NVENC reconfig',
      value: `${reconfigCount} 次`,
      tooltip: '累计 reconfigure 次数',
    });
    if (reconfigFail > 0) {
      fields.push({
        label: 'Reconfig 失败',
        value: reconfigFail,
        unit: '次',
        level: 'crit',
        tooltip: 'reconfigureEncoder 失败 — 历史 bug 监测',
      });
    }
  }

  return (
    <MetricGroup
      title="VBV / RC"
      icon={<Sliders size={14} />}
      level={cardLevel}
      primary={
        noVbv
          ? { label: '编码器', value: 'N/A' }
          : {
              label: '当前缓冲',
              value: vbvBuf > 0 ? `${vbvBuf}` : '—',
              unit: vbvBuf > 0 ? 'kb' : '',
              level: cappedLevel,
            }
      }
      fields={fields}
    />
  );
};

export default VbvRc;
